import React from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { LicenseInfo } from '@mui/x-license';
import { loadApp, setPageError } from "../store/app/actions";
import { incrementOutstandingRequestCount, decrementOutstandingRequestCount } from "../store/notifications/actions";
import { RootState, useAppSelector, useThunkDispatch } from "../store";
import { menuItems as defaultAdminMenuItems, default as Admin } from "../admin";
import Viewer from "../viewer";
import Public from "../public";
import LoginCallback from "../auth/LoginCallback";
import { fetchIntercept } from "../shared/monkey-patches";
import type { FetchInterceptor } from "../shared/services/fetchInterceptService";
import { useAuth } from "../auth/AuthContext";
import { SnackbarProvider } from 'notistack';
import Notifier from "../shared/components/Notifier";
import RequireAuthentication from "../auth/RequireAuthentication";
import Sidenav, { type MenuItem } from "../shared/components/Sidenav";
import UserPreferences from "../shared/pages/UserPreferences";
import { AppThemeProvider } from "../shared/providers/AppThemeProvider";
import { metastoreSupportsWebSockets } from "../shared/metastore-api-client";
import { MenuLocation } from "../shared/api-client";
import { PERMISSIONS } from "../auth/types";
import { useSettings } from "../shared/providers/SettingsProvider";
import RequiresTags from "../shared/components/RequiresTags";
import { ErrorContainer } from "../shared/pages/ErrorContainer";
import type { AppErrors } from "../store/app/types";
import _ from "lodash";
import { useSingleton } from "../shared/hooks";
import { WebSocketService } from "../shared/services/webSocketService";
import { WebSocketServiceProvider } from "../shared/providers/WebSocketServiceProvider";

LicenseInfo.setLicenseKey(
    "8762cbacf33ee1af1addc9c1f3492263Tz0xMzM4MDMsRT0xODIzNTU4Mzk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMS0yMDI2LFE9MSxBVD1zaW5nbGUsS1Y9Mg=="
);

const snackbarAnchorOrigin = { vertical: 'bottom', horizontal: 'right' } as const;

const App: React.FC = React.memo(function App() {
    const menuItems = useAppSelector(state => state.app.menuItems);
    const errors: AppErrors = useAppSelector(state => state.app.errors);
    const isLoading = useAppSelector(state => state.app.loading);
    const dispatch = useThunkDispatch();
    const { isAuthenticated, getTokenSilently, roles, user, effectiveTags } = useAuth();
    const [isInitialized, setIsInitialized] = React.useState(false);
    const settings = useSettings();

    // Keep a live reference to the latest settings so the fetch interceptor (registered once in the
    // effect below) always evaluates requests against the current branch-specific API URLs, which
    // change after authentication and whenever the user switches branches.
    const settingsRef = React.useRef(settings);
    settingsRef.current = settings;

    const webSocketService = useSingleton(() => {
        if (!isAuthenticated || !metastoreSupportsWebSockets()) return null;

        const result = new WebSocketService(
            `${settings.metastoreUrl}/hubs/events`,
            getTokenSilently as () => Promise<string>
        );
        result.connect();
        return result;
    });

    React.useEffect(() => {
        if (!isAuthenticated) return;

        // A request should carry the app's bearer token only when it targets one of our own APIs.
        // Settings are read from the ref on every call so that URL changes (after authentication and
        // on branch switches) take effect without re-registering the interceptor.
        //
        // The B2C authority origin (e.g. "https://qerent.b2clogin.com") is excluded: MSAL performs
        // its own token-exchange / refresh-token requests against it via the global fetch, so we
        // must NOT route them through the token-attaching interceptor: getTokenSilently() triggers
        // those very requests, so blocking on a token for them would deadlock (the request waits for
        // a token that is waiting for the request to finish).
        //
        // Falsy prefixes are ignored deliberately: an empty-string setting would otherwise make
        // `startsWith("")` match every URL (including MSAL's), reintroducing the deadlock above.
        const isAppApiRequest = (url?: string) => {
            if (typeof url !== "string") return false;

            const settings = settingsRef.current;
            const authAuthorityOrigin = settings.auth?.instance ? new URL(settings.auth.instance).origin : null;

            return !(authAuthorityOrigin && url.startsWith(authAuthorityOrigin)) &&
                ([window.location.origin, settings.metastoreUrl, settings.coreApiUrl, settings.dynamicDataApiUrl].some(prefix => prefix && url.startsWith(prefix)) || url.startsWith("/"));
        };

        // Register an HTTP interceptor that adds the latest JWT token to the request headers and tracks outstanding AJAX requests...
        const unregister = fetchIntercept.register({
            request: async (url, config) => {
                if (isAppApiRequest(url)) {
                    dispatch(incrementOutstandingRequestCount());

                    if (isAuthenticated && config != null) {
                        const token = await getTokenSilently();

                        // Backwards-compatibility shim for older versions of the Fetch API...
                        if (_.isFunction((config.headers as Headers).set)) {
                            (config.headers as Headers).set("Authorization", `Bearer ${token}`);
                        }
                        else {
                            config.headers["Authorization"] = `Bearer ${token}`;
                        }
                    }
                }

                return [url, config];
            },
            requestError: function (error) {
                dispatch(decrementOutstandingRequestCount());

                // Called when an error occured during another 'request' interceptor call
                return Promise.reject(error);
            },
            response: function (response) {
                if (isAppApiRequest(response?.url)) {
                    dispatch(decrementOutstandingRequestCount());

                    // Log failed requests to console.  App Insights collects all failed network requests, so this is purely for the benefit of the app developer.
                    if (response.status >= 400) {
                        console.error("API Request Failed:", response);
                    }
                }

                // Modify the reponse object
                return response;
            },
            responseError: function (error) {
                dispatch(decrementOutstandingRequestCount());

                // Log failed requests to console.  App Insights collects all failed network requests, so this is purely for the benefit of the app developer.
                console.error("API Request Failed:", error);

                // Handles fetch error...
                return Promise.reject(error);
            }
        } as FetchInterceptor);

        dispatch(loadApp({
                name: user.name,
                roles,
                effectiveTags,
                isDeveloper: effectiveTags?.includes?.(PERMISSIONS.DEVELOPER_TOOLS_ACCESS)
            },
            settings,
            settings.activeBranch,
        )); // Load the storylines and canvases for the current tenant once-off...

        setIsInitialized(true);

        return unregister;
    }, [isAuthenticated]);

    const { storylineMenuItems, adminMenuItems } = React.useMemo(() => {
        if (menuItems === "loading") return {
            storylineMenuItems: menuItems,
            adminMenuItems: defaultAdminMenuItems
        };

        return {
            storylineMenuItems: menuItems.filter(mi => mi.menuLocationId == MenuLocation.Storyline),
            adminMenuItems: [
                ...defaultAdminMenuItems,
                ...menuItems
                    .filter(mi => mi.menuLocationId == MenuLocation.Admin)
                    .map(mi => ({ ...mi, url: (mi.url.startsWith("/") && !mi.url.startsWith("/admin")) ? `/admin${mi.url}` : mi.url }))
            ]
        };
    }, [menuItems]);

    return (
        <AppThemeProvider>
            <WebSocketServiceProvider webSocketService={webSocketService}>
                <SnackbarProvider
                    anchorOrigin={snackbarAnchorOrigin}
                    maxSnack={5}
                >
                    <Notifier />
                    <ErrorContainer error={errors.global} includeNavBar>
                        <RouteChangeMonitor>
                            <Route path="login-callback" element={<LoginCallback />} />
                            <Route path="preferences" element={<PreferencesContent isInitialized={isInitialized && !isLoading} menuItems={storylineMenuItems} />} />
                            <Route path="public/*" element={<Public />} />
                            <Route path="admin/*" element={<AdminContent isInitialized={isInitialized && !isLoading} menuItems={adminMenuItems} />} />
                            <Route path="/*" element={<ViewerContent isInitialized={isInitialized && !isLoading} menuItems={storylineMenuItems} />} />
                        </RouteChangeMonitor>
                    </ErrorContainer>
                </SnackbarProvider>
            </WebSocketServiceProvider>
        </AppThemeProvider>
    );
});

const PreferencesContent: React.FC<{ isInitialized: boolean, menuItems: RootState["app"]["menuItems"] | "loading" }> = React.memo(function PreferencesContent({ isInitialized, menuItems }) {
    return (
        <RequireAuthentication isInitialized={isInitialized}>
            <div className="app-layout preferences-page">
                <Sidenav menuItems={menuItems} />
                <div className="content">
                    <UserPreferences />
                </div>
            </div>
        </RequireAuthentication>
    );
});

const AdminContent: React.FC<{ isInitialized: boolean, menuItems: MenuItem[] }> = React.memo(function AdminContent({ isInitialized, menuItems }) {
    return (
        <RequireAuthentication isInitialized={isInitialized}>
            <RequiresTags tags={[["users::read"], ["roles::read"], ["groups::read"]]}>
                <Admin menuItems={menuItems} />
            </RequiresTags>
        </RequireAuthentication>
    );
});

const ViewerContent: React.FC<{ isInitialized: boolean, menuItems: RootState["app"]["menuItems"] | "loading" }> = React.memo(function ViewerContent({ isInitialized, menuItems }) {
    return (
        <RequireAuthentication isInitialized={isInitialized}>
            <Viewer menuItems={menuItems} />
        </RequireAuthentication>
    );
});

const RouteChangeMonitor: React.FC<{ children: React.ReactNode }> = React.memo(function RouteChangeMonitor({ children }) {
    const location = useLocation();
    const dispatch = useThunkDispatch();

    // Reset the page error state when the user navigates to a new page...
    React.useEffect(() => {
        dispatch(setPageError(null));
    }, [location]);

    return (
        <Routes>
            {children}
        </Routes>
    );
});

export default App;